import { useEffect, useState } from "react";
import { Plus, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { KINDS } from "@/lib/constants";
import { SignMini } from "@/components/map/SignObjects";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export const saveTemplate = async (el) => {
  const { id, ...rest } = el;
  try {
    await api.post("/templates", rest);
    toast.success(`"${el.name}" guardado como plantilla`);
    window.dispatchEvent(new Event("templates-changed"));
  } catch (e) { toast.error(errMsg(e)); }
};

export const TemplatesPanel = ({ onAdd, canEdit, elements }) => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get("/templates").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    window.addEventListener("templates-changed", load);
    return () => window.removeEventListener("templates-changed", load);
  }, []);
  const used = new Set(elements.map((e) => `${e.kind}|${(e.name || "").toLowerCase()}`));
  const shown = items.filter((t) => !q || t.name.toLowerCase().includes(q.toLowerCase()));
  const del = async (t) => {
    try { await api.delete(`/templates/${t.id}`); toast.success("Plantilla eliminada"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-3" data-testid="templates-panel">
      <div className="text-[11px] text-slate-400">Cortes, zonas y señalización habituales. Pulse <b>+</b> para añadirlos al evento con su configuración.</div>
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar plantilla…" className="h-8 pl-8 text-xs" data-testid="templates-search-input" />
      </div>
      <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
        {!shown.length && <div className="text-xs text-slate-500 border border-dashed border-white/10 rounded-lg p-4 text-center" data-testid="templates-empty">Sin plantillas. Seleccione un elemento y pulse “Guardar como plantilla”, o use el botón “Plantilla” de la barra de objetos para guardar un recuadro de señales.</div>}
        {shown.map((t) => {
          const isUsed = used.has(`${t.kind}|${t.name.toLowerCase()}`);
          return (
            <div key={t.id} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/5 bg-[#0b1329]" data-testid={`template-item-${t.id}`}>
              {t.kind === "sign_group"
                ? <span className="flex -space-x-1 shrink-0">{t.items.slice(0, 3).map((it, i) => <SignMini key={i} type={it.sign_type} rot={it.rotation} size={14} />)}</span>
                : <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: KINDS[t.kind]?.color }} />}
              <span className="min-w-0 flex-1">
                <span className="block text-xs text-slate-200 truncate">{t.name}</span>
                <span className="block font-mono text-[10px] text-slate-500">{t.kind === "sign_group" ? `Señalización · ${t.items.length} objeto(s)` : KINDS[t.kind]?.short}{isUsed ? " · ya en el evento" : ""}</span>
              </span>
              {canEdit && (
                <>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-300" onClick={() => onAdd(t)} data-testid={`template-add-${t.id}`} title="Añadir al evento"><Plus className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 hover:text-red-300" onClick={() => del(t)} data-testid={`template-delete-${t.id}`} title="Eliminar plantilla"><Trash2 className="w-3.5 h-3.5" /></Button>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
